/**
 * OrderingTap — Ordenar palabras de una frase con tap (estilo Duolingo)
 * Las palabras se muestran abajo como chips; al pulsar, se añaden en orden a la zona de secuencia
 * Al pulsar un chip colocado, se devuelve al pool
 */

import { useState, useMemo, useId, useRef, useEffect } from 'react';
import { motion, LayoutGroup, useReducedMotion } from 'framer-motion';
import { motionPressWhileTap } from '@/constants/motionPress';
import { cn } from '@/utils/cn';
import { shuffle } from '@/utils/shuffle';
import type { OrderingActivity, OrderingAnswer } from '@/types/story.types';
import styles from '../StoryPage.module.scss';

interface PlacedItem {
  id: string;      // unique instance id (para layoutId)
  itemIndex: number; // índice del word en activity.items
  word: string;
}

interface PoolItem {
  id: string;
  itemIndex: number;
  word: string;
}

function makeId() {
  return `ord-${crypto.randomUUID().slice(0, 8)}`;
}


const ORDERING_TAP_FEEDBACK_DELAY_MS = 500;

interface OrderingTapProps {
  activity: OrderingActivity;
  answer: OrderingAnswer | undefined;
  onChange: (order: number[]) => void;
  onAllPlaced?: () => void;
  /** Array de booleanos indicando si cada posición en la secuencia es correcta */
  correctness?: boolean[];
  disabled?: boolean;
}

export function OrderingTap({
  activity,
  answer,
  onChange,
  onAllPlaced,
  correctness,
  disabled = false,
}: OrderingTapProps) {
  const reducedMotion = useReducedMotion();
  const tapFeedback = reducedMotion ? undefined : motionPressWhileTap;
  const layoutGroupId = useId();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Inicializar desde respuesta existente (si la hay)
  const [sequence, setSequence] = useState<PlacedItem[]>(() => {
    const savedOrder = answer?.order ?? [];
    return savedOrder.map((itemIndex) => ({
      id: makeId(),
      itemIndex,
      word: activity.items[itemIndex] ?? '',
    }));
  });

  const [pool, setPool] = useState<PoolItem[]>(() => {
    const savedOrder = answer?.order ?? [];
    const usedIndices = new Set(savedOrder);
    const remaining = activity.items
      .map((word, i) => ({ id: makeId(), itemIndex: i, word }))
      .filter((item) => !usedIndices.has(item.itemIndex));
    return shuffle(remaining);
  });

  const totalWords = useMemo(() => activity.items.length, [activity.items]);

  const handlePoolTap = (item: PoolItem) => {
    if (disabled) return;

    const newSequence = [...sequence, { id: item.id, itemIndex: item.itemIndex, word: item.word }];
    setSequence(newSequence);
    setPool((prev) => prev.filter((p) => p.id !== item.id));
    onChange(newSequence.map((s) => s.itemIndex));

    if (newSequence.length === totalWords && onAllPlaced) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onAllPlaced();
        timeoutRef.current = null;
      }, ORDERING_TAP_FEEDBACK_DELAY_MS);
    }
  };

  const handleSequenceTap = (item: PlacedItem) => {
    if (disabled) return;

    const newSequence = sequence.filter((s) => s.id !== item.id);
    setSequence(newSequence);
    setPool((prev) =>
      shuffle([...prev, { id: item.id, itemIndex: item.itemIndex, word: item.word }])
    );
    onChange(newSequence.map((s) => s.itemIndex));
  };

  return (
    <LayoutGroup id={layoutGroupId}>
      <div className={styles.story__orderingTap}>
        {/* Zona de secuencia construida */}
        <div className={styles.story__orderingTapSequence}>
          {sequence.map((item, i) => (
            <motion.button
              key={item.id}
              type="button"
              className={styles.story__orderingTapTag}
              onClick={() => handleSequenceTap(item)}
              disabled={disabled}
              whileTap={tapFeedback}
              layout
              aria-label={`Quitar "${item.word}" de la secuencia`}
            >
              <motion.span
                layoutId={item.id}
                className={cn(
                  styles.story__fillInBlankTapTagChip,
                  correctness && correctness[i] === true && styles['story__fillInBlankTapTagChip--correct'],
                  correctness && correctness[i] === false && styles['story__fillInBlankTapTagChip--incorrect']
                )}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              >
                {item.word}
              </motion.span>
            </motion.button>
          ))}
          {/* Placeholder vacío cuando aún no hay nada colocado */}
          {sequence.length === 0 && (
            <span className={styles.story__orderingTapEmpty} aria-hidden="true">
              &nbsp;
            </span>
          )}
        </div>

        {/* Pool de palabras disponibles */}
        <div className={styles.story__fillInBlankTapWords}>
          {pool.map((item) => (
            <motion.button
              key={item.id}
              type="button"
              className={styles.story__fillInBlankTapTag}
              onClick={() => handlePoolTap(item)}
              disabled={disabled}
              whileTap={tapFeedback}
              layout
              aria-label={`Añadir "${item.word}" a la secuencia`}
            >
              <motion.span
                layoutId={item.id}
                className={styles.story__fillInBlankTapTagChip}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              >
                {item.word}
              </motion.span>
            </motion.button>
          ))}
        </div>
      </div>
    </LayoutGroup>
  );
}
